import config from 'next/config';

import { Layer } from '@graphql';
import { defined, mkAssetUrl } from '@lib/utils';
import getLayerStore from '@stores/LayerStore';

import type { LayerInfo } from '@lib/types';
import type { Maybe } from '@graphql';
import type { ApolloClient } from '@lib/apolloClient';
import type { MapAddress } from './MapAddress';

interface Props {
  url: string;
  layerPath: (Maybe<string> | undefined)[];
  layerPathSlugs?: (Maybe<string> | undefined)[];
}

const LayerStore = getLayerStore();

export class MapPlace implements LayerInfo {
  static async fromMapAddress(
    mapAddress: MapAddress,
    apolloClient: ApolloClient,
  ): Promise<MapPlace> {
    const { layersByCoordinates } = await LayerStore.getLayersByCoordinates(
      apolloClient,
      mapAddress.location.lat(),
      mapAddress.location.lng(),
      mapAddress.layer,
    );

    return new MapPlace({
      url: layersByCoordinates?.url || '/',
      layerPath: [
        layersByCoordinates?.stateName,
        layersByCoordinates?.cityName,
        layersByCoordinates?.districtName,
        layersByCoordinates?.districtSmallName,
      ],
    });
  }

  static async fromUrl(url: string, apolloClient: ApolloClient): Promise<MapPlace | null> {
    const props: Props = {
      url: '/',
      layerPath: [],
    };

    const pathWithoutPrefix =
      url?.substring(config().publicRuntimeConfig.assetPrefix.length) || '/';
    const [urlPath] = pathWithoutPrefix.split('?');

    if (urlPath !== '/' && urlPath !== '') {
      const layers = await LayerStore.getLayersByUrl(apolloClient, urlPath);
      if (!layers) {
        return null;
      }

      props.url = layers.url;
      props.layerPath = [
        layers.stateName,
        layers.cityName,
        layers.districtName,
        layers.districtSmallName,
      ];
      props.layerPathSlugs = [
        layers.stateNameSlug,
        layers.cityNameSlug,
        layers.districtNameSlug,
        layers.districtSmallNameSlug,
      ];
    }

    return new MapPlace(props);
  }

  private _url: string;
  private _layerPath: string[];
  private _layerPathSlugs: string[];

  private constructor({ url, layerPath, layerPathSlugs }: Props) {
    this._url = url;
    this._layerPath = layerPath.filter(defined);
    this._layerPathSlugs = layerPathSlugs ? layerPathSlugs.filter(defined) : [];
  }

  toJson(): LayerInfo {
    return {
      layerType: this.layerType,
      layerName: this.layerName,
      url: this.url,
      state: this.state,
      stateSlug: this.stateSlug,
      city: this.city,
      citySlug: this.citySlug,
      district: this.district,
      districtSlug: this.districtSlug,
      districtSmall: this.districtSmall,
      districtSmallSlug: this.districtSmallSlug,
    };
  }

  get layerType(): Layer {
    switch (this._layerPath.length) {
      case 1:
        return Layer.State;
      case 2:
        return Layer.City;
      case 3:
        return Layer.District;
      case 4:
        return Layer.DistrictSmall;
      default:
        return Layer.Country;
    }
  }

  get layerName(): string {
    return this._layerPath[this._layerPath.length - 1] || 'de';
  }

  get url(): string {
    return mkAssetUrl(this._url);
  }

  get state(): string | undefined {
    return this._layerPath[0];
  }

  get stateSlug(): string | undefined {
    return this._layerPathSlugs[0];
  }

  get city(): string | undefined {
    return this._layerPath[1];
  }

  get citySlug(): string | undefined {
    return this._layerPathSlugs[1];
  }

  get district(): string | undefined {
    return this._layerPath[2];
  }

  get districtSlug(): string | undefined {
    return this._layerPathSlugs[2];
  }

  get districtSmall(): string | undefined {
    return this._layerPath[3];
  }

  get districtSmallSlug(): string | undefined {
    return this._layerPathSlugs[3];
  }
}
